import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// A saved `order` array predates any column added after the user first
// opened the table — the table then has to guess where to put it (in
// practice, at the very end), which drifts from wherever the column sits in
// the live `columns` definition. Splice it in right after a specific
// neighbour instead of leaving that to guesswork.
//
// No-op if `order` is empty (fresh user — the live columns array order
// already wins) or already contains `key` (user has an opinion, don't
// override a manual drag).
export function insertColumnAfter(order, key, afterKey) {
  if (!Array.isArray(order) || order.length === 0) return order;
  if (order.includes(key)) return order;
  const afterIndex = order.indexOf(afterKey);
  if (afterIndex === -1) return order;
  const next = [...order];
  next.splice(afterIndex + 1, 0, key);
  return next;
}

// Same as insertColumnAfter, but splices `key` in right before `beforeKey`.
export function insertColumnBefore(order, key, beforeKey) {
  if (!Array.isArray(order) || order.length === 0) return order;
  if (order.includes(key)) return order;
  const beforeIndex = order.indexOf(beforeKey);
  if (beforeIndex === -1) return order;
  const next = [...order];
  next.splice(beforeIndex, 0, key);
  return next;
}

// Load persisted column state ({hidden, pinned, order}) from localStorage and
// shape it for `useTableColumns(...)`. Also supports a one-time migration from
// the legacy {key: bool} `visibleColumns` payload — pass `legacyKey` to convert
// and clean it up on read.
export function loadColumnsStateFromLS(storageKey, defaults = {}, legacyKey = null) {
  const out = {
    defaultHidden: defaults.defaultHidden ?? [],
    defaultPinned: defaults.defaultPinned ?? [],
    defaultOrder: defaults.defaultOrder ?? [],
  };
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      const saved = JSON.parse(raw);
      if (saved && typeof saved === 'object') {
        if (Array.isArray(saved.hidden)) out.defaultHidden = saved.hidden;
        if (Array.isArray(saved.pinned)) out.defaultPinned = saved.pinned;
        if (Array.isArray(saved.order)) out.defaultOrder = saved.order;
        return out;
      }
    }
    if (legacyKey) {
      const legacyRaw = localStorage.getItem(legacyKey);
      if (legacyRaw) {
        const legacy = JSON.parse(legacyRaw);
        if (legacy && typeof legacy === 'object') {
          out.defaultHidden = Object.entries(legacy)
            .filter(([, v]) => v === false)
            .map(([k]) => k);
          // Migrate forward
          localStorage.setItem(
            storageKey,
            JSON.stringify({ hidden: out.defaultHidden, pinned: out.defaultPinned, order: out.defaultOrder })
          );
          localStorage.removeItem(legacyKey);
        }
      }
    }
  } catch {
    // ignore
  }
  return out;
}

// Local fallback that mirrors @anyemp/ui-kit's `useTableColumns`. Returns props
// to spread into <Table> — defaults to seed the table's internal state plus the
// matching on*Change callbacks.
//
// Persistence runs in a single useEffect that depends on all three state
// slices, not from inside the per-callback handlers. This avoids the
// closure-stale-snapshot trap: when Table fires multiple callbacks in one
// tick (e.g. pinning a column also reorders it; hiding a pinned column
// auto-unpins AND reorders it), per-callback writes would overwrite each
// other with stale siblings. The effect fires once after React commits all
// the setX calls, with the consistent post-commit snapshot.
export function useTableColumns(_columns, options = {}) {
  const { defaultHidden, defaultPinned, defaultOrder, onChange } = options;

  const [hidden, setHidden] = useState(() => defaultHidden ?? []);
  const [pinned, setPinned] = useState(() => defaultPinned ?? []);
  const [order, setOrder] = useState(() => defaultOrder ?? []);

  // Initial defaults — frozen at mount, fed to <Table /> via default* props.
  const initialDefaultsRef = useRef({
    hidden: defaultHidden ?? [],
    pinned: defaultPinned ?? [],
    order: defaultOrder ?? [],
  });

  // Always hand the effect the latest onChange identity without making the
  // effect re-fire on every parent render (consumers typically pass an
  // inline arrow with fresh identity each render).
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });

  // Skip the first effect run — don't rewrite localStorage with the seed
  // we just read from it.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    onChangeRef.current?.({ hidden, pinned, order });
  }, [hidden, pinned, order]);

  // Per-axis callbacks are pure state setters — no closure-read writes.
  const onHiddenColumnsChange = useCallback((next) => setHidden(next), []);
  const onStickyColumnsChange = useCallback((next) => setPinned(next), []);
  const onColumnOrderChange = useCallback((next) => setOrder(next), []);

  const tableProps = useMemo(() => ({
    defaultHiddenColumns: initialDefaultsRef.current.hidden,
    defaultStickyColumns: initialDefaultsRef.current.pinned,
    defaultColumnOrder: initialDefaultsRef.current.order,
    columnMenuDefaults: {
      hidden: initialDefaultsRef.current.hidden,
      pinned: initialDefaultsRef.current.pinned,
      order: initialDefaultsRef.current.order,
    },
  }), []);

  return {
    ...tableProps,
    onHiddenColumnsChange,
    onStickyColumnsChange,
    onColumnOrderChange,
    state: { hidden, pinned, order },
    setHidden,
    setPinned,
    setOrder,
  };
}
